import { useState, useEffect, useRef } from "react";
import React from "react";
import { db } from "../firebase";
import { collection, query, where, orderBy, onSnapshot, addDoc, deleteDoc, doc, serverTimestamp } from "firebase/firestore";
import Icon from "./Icon";

const MATERIAUX_SORTANTS = ["Excavation","Souche","Béton propre","Béton contaminé","Asphalte","Autre"];
const MATERIAUX_ENTRANTS = ["Gravier 0-3/4\"","Pierre nette 1/4\"","Pierre nette 1/2\"","Pierre nette 3/4\"","Sable classe A","Sable classe B","Poussière de pierre","Terre à gazon","Remblais","Pierre 4\"-8\"","Autre"];

export default function TruckLog({ jobId, jobTitle, myName, canEdit, lang, onEventsUpdate, selectedDate }) {
  const todayStr = new Date().toISOString().slice(0, 10);
  const [navDate, setNavDate] = useState(selectedDate || todayStr);
  const [direction, setDirection] = useState("out");
  const [materialType, setMaterialType] = useState(MATERIAUX_SORTANTS[0]);
  const [tonnage, setTonnage] = useState(15);
  const [pending, setPending] = useState(false);
  const [flash, setFlash] = useState(false);
  const [events, setEvents] = useState([]);
  const dateInputRef = useRef(null);

  const isToday = navDate === todayStr;
  const isFr = lang !== "en";

  const tt = isFr ? {
    entrant: "Entrant",
    sortant: "Sortant",
    materiau: "Matériau",
    tonnage: "Tonnage",
    logBtn: "Enregistrer ce voyage",
    voyages: "voyages",
    noVoyages: "Aucun voyage enregistré",
    recentLog: "Enregistrements",
  } : {
    entrant: "Inbound",
    sortant: "Outbound",
    materiau: "Material",
    tonnage: "Tonnage",
    logBtn: "Log this load",
    voyages: "loads",
    noVoyages: "No loads recorded",
    recentLog: "Log",
  };

  const currentMats = direction === "out" ? MATERIAUX_SORTANTS : MATERIAUX_ENTRANTS;

  const fmtNavDate = (ds) => {
    const d = new Date(ds + "T12:00:00");
    return d.toLocaleDateString(isFr ? "fr-CA" : "en-CA", { day: "numeric", month: "long" });
  };

  const shiftDate = (n) => {
    const d = new Date(navDate + "T12:00:00");
    d.setDate(d.getDate() + n);
    const s = d.toISOString().slice(0, 10);
    if (s <= todayStr) setNavDate(s);
  };

  useEffect(() => {
    if (!jobId) return;
    const q = query(
      collection(db, "productionEvents"),
      where("jobId", "==", jobId),
      where("eventType", "==", "truck"),
      where("date", "==", navDate),
      orderBy("loggedAt", "asc")
    );
    const unsub = onSnapshot(q, { includeMetadataChanges: true }, snap => {
      const data = snap.docs.map(d => {
        const raw = d.data();
        return { id: d.id, ...raw, _pending: d.metadata.hasPendingWrites };
      });
      setEvents(data);
      if (onEventsUpdate) onEventsUpdate(data);
    });
    return unsub;
  }, [jobId, navDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDirectionChange = (dir) => {
    setDirection(dir);
    setMaterialType(dir === "out" ? MATERIAUX_SORTANTS[0] : MATERIAUX_ENTRANTS[0]);
  };

  const handleLog = async () => {
    if (!canEdit || pending || !isToday) return;
    setPending(true);
    try {
      await addDoc(collection(db, "productionEvents"), {
        jobId,
        jobTitle: jobTitle || "",
        date: todayStr,
        eventType: "truck",
        direction,
        materialType,
        tonnage: parseFloat(tonnage) || 15,
        unit: "t.m.",
        loggedBy: myName,
        loggedAt: serverTimestamp(),
        loggedAtISO: new Date().toISOString(),
      });
      setFlash(true);
      setTonnage(15);
      setTimeout(() => setFlash(false), 1500);
    } catch (err) { console.error(err); }
    setPending(false);
  };

  const handleDelete = async (id) => {
    try { await deleteDoc(doc(db, "productionEvents", id)); } catch (err) { console.error(err); }
  };

  const inEvents = events.filter(e => e.direction === "in");
  const outEvents = events.filter(e => e.direction === "out");
  const sumTonnage = (evts) => evts.reduce((a, e) => a + (parseFloat(e.tonnage) || 0), 0);

  const breakdown = (evts) => {
    const map = {};
    evts.forEach(e => {
      if (!map[e.materialType]) map[e.materialType] = { count: 0, tonnage: 0 };
      map[e.materialType].count++;
      map[e.materialType].tonnage += parseFloat(e.tonnage) || 0;
    });
    return map;
  };

  const summaryLabel = isToday ? (isFr ? "Aujourd'hui" : "Today") : fmtNavDate(navDate);

  return (
    <div style={{ marginBottom: 24 }}>

      {/* Date nav bar */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", padding: "8px 12px" }}>
        <button className="btn-icon" onClick={() => shiftDate(-1)}>
          <span style={{ display: "inline-flex", transform: "rotate(180deg)" }}><Icon name="chevronRight" size={16} /></span>
        </button>
        <div
          style={{ flex: 1, textAlign: "center", position: "relative", cursor: "pointer" }}
          onClick={() => dateInputRef.current?.showPicker?.() ?? dateInputRef.current?.focus?.()}
        >
          <div style={{ fontWeight: 700, fontSize: 13 }}>{summaryLabel}</div>
          <input
            type="date"
            ref={dateInputRef}
            value={navDate}
            max={todayStr}
            onChange={e => { if (e.target.value && e.target.value <= todayStr) setNavDate(e.target.value); }}
            style={{ position: "absolute", opacity: 0, pointerEvents: "none", top: 0, left: "50%", width: 1, height: 1 }}
          />
        </div>
        <button className="btn-icon" onClick={() => shiftDate(1)} disabled={isToday} style={{ opacity: isToday ? 0.3 : 1 }}>
          <Icon name="chevronRight" size={16} />
        </button>
      </div>

      {/* Direction toggle — today only */}
      {isToday && (
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          <button
            onClick={() => handleDirectionChange("in")}
            style={{
              flex: 1, padding: "14px 8px", fontSize: 15, fontWeight: 700,
              border: direction === "in" ? "none" : "2px solid var(--border)",
              borderRadius: "var(--radius)",
              background: direction === "in" ? "var(--red)" : "transparent",
              color: direction === "in" ? "#fff" : "var(--text-3)",
              cursor: "pointer",
            }}
          >
            ↓ {tt.entrant.toUpperCase()}
          </button>
          <button
            onClick={() => handleDirectionChange("out")}
            style={{
              flex: 1, padding: "14px 8px", fontSize: 15, fontWeight: 700,
              border: direction === "out" ? "none" : "2px solid var(--border)",
              borderRadius: "var(--radius)",
              background: direction === "out" ? "var(--red)" : "transparent",
              color: direction === "out" ? "#fff" : "var(--text-3)",
              cursor: "pointer",
            }}
          >
            ↑ {tt.sortant.toUpperCase()}
          </button>
        </div>
      )}

      {/* Material select — today only */}
      {isToday && (
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, color: "var(--text-3)", display: "block", marginBottom: 4 }}>{tt.materiau}</label>
          <select
            className="form-select"
            style={{ fontSize: 16 }}
            value={materialType}
            onChange={e => setMaterialType(e.target.value)}
          >
            {currentMats.map(m => <option key={m}>{m}</option>)}
          </select>
        </div>
      )}

      {/* Tonnage row — today only */}
      {isToday && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          <label style={{ fontSize: 12, color: "var(--text-3)", whiteSpace: "nowrap" }}>{tt.tonnage}</label>
          <input
            type="number"
            className="form-input"
            style={{ width: 80, fontSize: 16, textAlign: "center" }}
            min={0.5}
            step={0.5}
            value={tonnage}
            onChange={e => setTonnage(e.target.value)}
            onFocus={e => e.target.select()}
          />
          <span style={{ fontSize: 13, color: "var(--text-3)" }}>t.m.</span>
        </div>
      )}

      {/* Submit button — today only */}
      {isToday && canEdit && (
        <button
          onClick={handleLog}
          disabled={pending}
          style={{
            width: "100%", minHeight: 56, fontSize: 15, fontWeight: 700,
            background: "var(--red)", color: "#fff", border: "none",
            borderRadius: "var(--radius)", cursor: pending ? "not-allowed" : "pointer",
            opacity: pending ? 0.7 : 1, marginBottom: 16,
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          }}
        >
          {flash ? "✓" : <><Icon name="plus" size={16} />+ {tt.logBtn}</>}
        </button>
      )}

      {/* Summary */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 12, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>
          {summaryLabel}
        </div>
        {events.length === 0 && (
          <div style={{ fontSize: 12, color: "var(--text-3)", fontStyle: "italic" }}>{tt.noVoyages}</div>
        )}
        {/* Inbound */}
        <div style={{ fontSize: 13, marginBottom: 2 }}>
          <span style={inEvents.length > 0 ? { fontWeight: 700 } : { color: "var(--text-3)" }}>
            ↓ {tt.entrant}: {inEvents.length} {tt.voyages} · {sumTonnage(inEvents).toFixed(1)} t.m.
          </span>
        </div>
        {inEvents.length > 0 && Object.entries(breakdown(inEvents)).map(([mat, info]) => (
          <div key={mat} style={{ fontSize: 12, color: "var(--text-3)", paddingLeft: 14, marginBottom: 2 }}>
            {mat}: {info.count} {tt.voyages} · {info.tonnage.toFixed(1)} t.m.
          </div>
        ))}
        {/* Outbound */}
        <div style={{ fontSize: 13, marginTop: 6, marginBottom: 2 }}>
          <span style={outEvents.length > 0 ? { fontWeight: 700 } : { color: "var(--text-3)" }}>
            ↑ {tt.sortant}: {outEvents.length} {tt.voyages} · {sumTonnage(outEvents).toFixed(1)} t.m.
          </span>
        </div>
        {outEvents.length > 0 && Object.entries(breakdown(outEvents)).map(([mat, info]) => (
          <div key={mat} style={{ fontSize: 12, color: "var(--text-3)", paddingLeft: 14, marginBottom: 2 }}>
            {mat}: {info.count} {tt.voyages} · {info.tonnage.toFixed(1)} t.m.
          </div>
        ))}
      </div>

      {/* Log */}
      {events.length > 0 && (
        <div>
          <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 12, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>
            {tt.recentLog}
          </div>
          {events.map(e => (
            <div key={e.id} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, padding: "5px 0", borderBottom: "1px solid var(--border)" }}>
              <span style={{ color: "var(--text-3)", minWidth: 38 }}>
                {e.loggedAt?.seconds
                  ? new Date(e.loggedAt.seconds * 1000).toLocaleTimeString("fr-CA", { hour: "2-digit", minute: "2-digit" })
                  : e.loggedAtISO
                  ? new Date(e.loggedAtISO).toLocaleTimeString("fr-CA", { hour: "2-digit", minute: "2-digit" })
                  : "—"}
              </span>
              <span style={{ color: "var(--text-3)" }}>·</span>
              <span style={{ fontWeight: 600, flex: 1 }}>{e.loggedBy}</span>
              <span>{e.direction === "in" ? "↓" : "↑"} {e.materialType}</span>
              <span style={{ color: "var(--text-3)" }}>· {e.tonnage} t.m.</span>
              {canEdit && isToday && (
                <button className="btn-icon danger" title="Supprimer" onClick={() => handleDelete(e.id)} style={{ padding: 2 }}>
                  <Icon name="trash" size={12} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
